import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { MerchantCardOptionItem } from '@manage-cards/components/composed/merchantCardList/MerchantCardList';
import { Global } from '@manage-cards/constants';
import Api from '@manage-cards/services/Api';
import {
	CardDeleteStatus,
	CardFilterType,
	ICardListResponse,
	ICardMerchantData,
	IMerchantCardData,
} from '@manage-cards/types';

export const useManageCardsLayout = ({
	isLoggedIn,
	isInitializing,
}: {
	isLoggedIn: boolean;
	isInitializing: boolean;
}) => {
	const [loading, setLoading] = useState(true);
	const [filterType, setFilterType] = useState(CardFilterType.BY_CARD);
	const [filteredData, setFilteredData] = useState<
		Array<IMerchantCardData | ICardMerchantData>
	>([]);
	const [currentSelectedData, setCurrentSelectedData] = useState<
		ICardMerchantData | IMerchantCardData
	>();
	const cardListMasterData = useRef<ICardListResponse>();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [cardDataToDelete, setCardDataToDelete] = useState<{
		cardId: string;
		merchantId: string;
		status: CardDeleteStatus;
	}>({ cardId: '', merchantId: '', status: CardDeleteStatus.UNSET });

	const optionItemList: Array<MerchantCardOptionItem> = useMemo(() => {
		if (filterType === CardFilterType.BY_CARD) {
			const data = filteredData as ICardMerchantData[];
			return data.map((item) => ({
				id: item.lastFourDigits,
				selected:
					(currentSelectedData as ICardMerchantData)?.lastFourDigits ===
					item.lastFourDigits,

				title: `${item.bankName} XX${item.lastFourDigits} (${item.savedCards.length ?? 0})`,
				icon: item.cardLogoUrl,
			}));
		}
		const data = filteredData as IMerchantCardData[];
		return data.map((item) => ({
			id: item.merchantId,
			selected:
				(currentSelectedData as IMerchantCardData)?.merchantId ===
				item.merchantId,
			title: `${item.merchantName} (${item.savedCards.length ?? 0})`,
			icon: `${Global.MERCHANT_LOGO_BASE_URL}/${item.merchantId}.png`,
		}));
	}, [filterType, filteredData, currentSelectedData]);

	const setData = () => {
		if (cardListMasterData.current === undefined) return;
		if (filterType === CardFilterType.BY_CARD) {
			setFilteredData(cardListMasterData.current.cardMerchantData);
			setCurrentSelectedData(cardListMasterData.current.cardMerchantData[0]);
		} else if (filterType === CardFilterType.BY_SELLER) {
			setFilteredData(cardListMasterData.current.merchantCardData);
			setCurrentSelectedData(cardListMasterData.current.merchantCardData[0]);
		}
	};

	const fetchCardListData = async () => {
		try {
			setLoading(true);
			const response = await Api.getUserCards();
			cardListMasterData.current = response;
			setData();
		} catch (error) {
			console.error('Error fetching card list data:', error);
		} finally {
			setLoading(false);
		}
	};

	const onOptionItemSelect = (itemId: string) => {
		if (filterType === CardFilterType.BY_CARD) {
			const data = filteredData as ICardMerchantData[];
			const selectedData = data.find((item) => item.lastFourDigits === itemId);
			if (selectedData) {
				setCurrentSelectedData(selectedData);
			}
		} else {
			const data = filteredData as IMerchantCardData[];
			const selectedData = data.find((item) => item.merchantId === itemId);
			if (selectedData) {
				setCurrentSelectedData(selectedData);
			}
		}
	};

	useEffect(() => {
		if (!isLoggedIn || isInitializing) return;
		fetchCardListData();
	}, [isLoggedIn, isInitializing]);

	useEffect(() => {
		setData();
	}, [filterType]);

	const showDeleteConfirmation = (cardId: string, merchantId: string) => {
		setShowConfirmModal(true);
		setCardDataToDelete({ cardId, merchantId, status: CardDeleteStatus.UNSET });
	};

	const handleCardDelete = async () => {
		if (!cardDataToDelete?.cardId || !cardDataToDelete?.merchantId) return;

		const { cardId, merchantId } = cardDataToDelete;
		try {
			setCardDataToDelete((prev) => ({
				...prev,
				status: CardDeleteStatus.INPROGRESS,
			}));
			await Api.deleteUserCard(cardId, merchantId);
			setCardDataToDelete((prev) => ({
				...prev,
				status: CardDeleteStatus.SUCCESS,
			}));
			// Refetch the card list data after deletion
			fetchCardListData();
		} catch (error) {
			console.error('Error deleting card:', error);
			setCardDataToDelete((prev) => ({
				...prev,
				status: CardDeleteStatus.FAILURE,
			}));
		}
	};

	return {
		loading,
		filterType,
		filteredData,
		currentSelectedData,
		optionItemList,
		showConfirmModal,
		cardDataToDelete,
		handleCardDelete,
		setFilterType,
		onOptionItemSelect,
		setShowConfirmModal,
		showDeleteConfirmation,
	};
};
